import React, { useEffect, useState } from "react";
import axios from "axios";
import { Landmark, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  ["bank_name", "Банк", "Например: АО «Т-Банк»", 200],
  ["bik", "БИК (9 цифр)", "044525974", 9],
  ["account", "Расчётный счёт (20 цифр)", "40802810...", 20],
  ["corr_account", "Корр. счёт (20 цифр)", "30101810...", 20],
];
const EMPTY = { bank_name: "", bik: "", account: "", corr_account: "" };

// T4-2: необязательные реквизиты для счёта B2B (хранятся зашифрованно, видны только владельцу и в его счетах)
export const BankDetailsBlock = ({ showToast }) => {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [consent, setConsent] = useState(false);

  const load = () => axios.get(`${API}/b2b/profile/bank-details`).then((r) => setCurrent(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, max) => (e) => setForm({ ...form, [k]: k === "bank_name" ? e.target.value.slice(0, max) : e.target.value.replace(/\D/g, "").slice(0, max) });

  const save = async () => {
    const body = { ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "")), pd_consent: consent, consent_version: current?.consent_version };
    setSaving(true);
    try {
      const r = await axios.put(`${API}/b2b/profile/bank-details`, body);
      setCurrent(r.data); setForm(EMPTY); setConsent(false);
      showToast("Реквизиты сохранены", "success");
    } catch (e) {
      showToast(e.response?.data?.detail || "Не удалось сохранить реквизиты", "error");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Удалить реквизиты для оплаты? В новых счетах снова будет строка «реквизиты предоставит отдельно (в чате)».")) return;
    try {
      const r = await axios.delete(`${API}/b2b/profile/bank-details`);
      setCurrent(r.data); setForm(EMPTY);
      showToast("Реквизиты удалены", "success");
    } catch (e) {
      showToast(e.response?.data?.detail || "Не удалось удалить реквизиты", "error");
    }
  };

  const has = current?.has_bank_details;
  return (
    <div className="bg-white rounded-xl p-5 space-y-3 mt-4" style={{ border: "1px solid var(--rv-border)" }} data-testid="bank-details-block">
      <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "var(--rv-navy)" }}><Landmark size={18} style={{ color: "var(--rv-teal)" }} /> Реквизиты для оплаты (необязательно)</h3>
      <p className="text-xs flex items-start gap-1.5 p-2.5 rounded-lg" style={{ background: "#F0FDFA", border: "1px solid #99F6E4", color: "#134E4A" }} data-testid="bank-details-privacy-note">
        <Lock size={13} className="mt-0.5 flex-shrink-0" /> Хранится в зашифрованном виде, виден только вам и попадает в счета ваших B2B-заданий. Реквизиты появятся в счетах, сформированных после сохранения.
      </p>
      {has && (
        <div className="text-sm p-3 rounded-lg" style={{ background: "#F8FAFC", border: "1px solid var(--rv-border)" }} data-testid="bank-details-current">
          {current.bank_name && <div>Банк: <strong>{current.bank_name}</strong></div>}
          {current.bik && <div>БИК: {current.bik}</div>}
          {current.account_masked && <div data-testid="bank-details-account-masked">Р/с: {current.account_masked}</div>}
          {current.corr_account_masked && <div>К/с: {current.corr_account_masked}</div>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, ph, max]) => (
          <div key={k}>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--rv-text-secondary)" }}>{label}</label>
            <input value={form[k]} onChange={set(k, max)} placeholder={has ? "Оставьте пустым, чтобы не менять" : ph} inputMode={k === "bank_name" ? "text" : "numeric"}
              className="w-full px-3 py-2 rounded-lg text-sm" style={{ border: "1px solid var(--rv-border)" }} data-testid={`bank-details-${k.replace("_", "-")}-input`} />
          </div>
        ))}
      </div>
      {/* TZ8-13: отдельное согласие 152-ФЗ (версия текста фиксируется в consent_logs) */}
      <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: "var(--rv-text-secondary)" }} data-testid="bank-details-consent-label">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 flex-shrink-0" data-testid="bank-details-consent-checkbox" />
        <span>
          {current?.consent_text || "Даю согласие на обработку банковских реквизитов в соответствии с Политикой конфиденциальности (152-ФЗ)."}{" "}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--rv-teal)" }}>Политика конфиденциальности</a>.
          {current?.consent_version && <span className="block mt-0.5" style={{ color: "var(--rv-text-muted)" }} data-testid="bank-details-consent-version">Редакция согласия от {current.consent_version.split("-").reverse().join(".")}</span>}
        </span>
      </label>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving || !consent || !Object.values(form).some(Boolean)} className="reversia-btn reversia-btn-teal reversia-btn-md" data-testid="bank-details-save-btn">
          {saving ? "Сохранение..." : "Сохранить реквизиты"}
        </button>
        {has && <button onClick={remove} className="reversia-btn reversia-btn-md" style={{ border: "1px solid var(--rv-border)" }} data-testid="bank-details-delete-btn">Удалить реквизиты</button>}
      </div>
    </div>
  );
};
